import { Box, Card, CardActionArea, Typography } from "@mui/material";
import { t } from "@lingui/macro";
import {
  FitnessProfileResponseType,
  WahooWorkoutTypeFamily,
} from "@WahooFitness/cloud-client-types";
import { getWorkoutTypeFamilyIcon } from "@/services/getWorkoutTypeIcon";
import useLocalizedCyclingFitnessData from "../FitnessProfile/CyclingProfile/useLocalizedCyclingFitnessData";
import { useNavigate } from "react-router";
import { RedesignrChip, WfSnail, WfSnailVariant } from "@WahooFitness/redesignr";
import { useLingui } from "@lingui/react";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { ClickTarget, EventType, SourcePage } from "@/services/Analytics/EventTypes";
import { useCallback, useMemo } from "react";
import useLocalizedRunningFitnessData from "../FitnessProfile/RunningProfile/useLocalizedRunningFitnessData";
import { useSettingsVersionedFeatures } from "@/hooks/useSettingsVersionedFeatures";

interface AthleteProfilePreviewCardProps {
  workoutFamily: WahooWorkoutTypeFamily.Cycling | WahooWorkoutTypeFamily.Running;
  fitnessProfile?: FitnessProfileResponseType;
}

function getWorkoutFamilyName(
  family: WahooWorkoutTypeFamily.Cycling | WahooWorkoutTypeFamily.Running
): string {
  switch (family) {
    case WahooWorkoutTypeFamily.Cycling:
      return t`Cycling`;
    case WahooWorkoutTypeFamily.Running:
      return t`Running`;
  }
}

function AthleteProfilePreviewCard({
  workoutFamily,
  fitnessProfile,
}: AthleteProfilePreviewCardProps) {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const { logEvent } = useLogEvent();
  const { enabled: showBetaTag } = useSettingsVersionedFeatures(
    "SHOW_RUNNING_ATHLETE_PROFILE_BETA_TAG"
  );
  const isRunning = workoutFamily === WahooWorkoutTypeFamily.Running;
  const { getRiderTypeById } = useLocalizedCyclingFitnessData();
  const { getRunnerTypeById } = useLocalizedRunningFitnessData();
  const SportIcon = getWorkoutTypeFamilyIcon(workoutFamily);
  const fitnessProfileType = useMemo(() => {
    switch (workoutFamily) {
      case WahooWorkoutTypeFamily.Cycling:
        return getRiderTypeById(fitnessProfile?.fitness_type_id || undefined);
      case WahooWorkoutTypeFamily.Running:
        return getRunnerTypeById(fitnessProfile?.fitness_type_id || undefined);
    }
  }, [workoutFamily, fitnessProfile, getRiderTypeById, getRunnerTypeById]);

  const hasProfile = !!fitnessProfile?.fitness_type_id;
  const familyName = getWorkoutFamilyName(workoutFamily);

  const dimensions = useMemo(() => {
    switch (workoutFamily) {
      case WahooWorkoutTypeFamily.Cycling:
        return {
          ftp: { value: fitnessProfile?.dim_1 ?? 0, rank: fitnessProfile?.dim_1_rank ?? 0 },
          map: { value: fitnessProfile?.dim_2 ?? 0, rank: fitnessProfile?.dim_2_rank ?? 0 },
          ac: { value: fitnessProfile?.dim_3 ?? 0, rank: fitnessProfile?.dim_3_rank ?? 0 },
          nm: { value: fitnessProfile?.dim_4 ?? 0, rank: fitnessProfile?.dim_4_rank ?? 0 },
        };
      case WahooWorkoutTypeFamily.Running:
        return {
          tp: { value: fitnessProfile?.dim_1 ?? 0, rank: fitnessProfile?.dim_1_rank ?? 0 },
          map: { value: fitnessProfile?.dim_2 ?? 0, rank: fitnessProfile?.dim_2_rank ?? 0 },
          ac: { value: fitnessProfile?.dim_3 ?? 0, rank: fitnessProfile?.dim_3_rank ?? 0 },
        };
    }
  }, [workoutFamily, fitnessProfile]);

  const clickTarget = useMemo(
    () =>
      workoutFamily === WahooWorkoutTypeFamily.Cycling
        ? ClickTarget.athlete_profile_cycling
        : ClickTarget.athlete_profile_running,
    [workoutFamily]
  );

  const handleCardClick = useCallback(() => {
    logEvent("settings_menu", {
      event_type: EventType.click,
      click_target: clickTarget,
      source_page_name: SourcePage.settings,
    });
    navigate(`/fitness-profile?workout_type_family_id=${workoutFamily}`);
  }, [clickTarget, logEvent, navigate, workoutFamily]);

  return (
    // TODO: Refactor to use WfCard once it's available in redesignr - currently using MUI Card for styling purposes
    <Card
      elevation={4}
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        marginBottom: 1,
      }}
      data-testid="athleteProfilePreviewCard"
    >
      <CardActionArea onClick={handleCardClick} sx={{ position: "relative" }}>
        {isRunning && showBetaTag && (
          <RedesignrChip
            label={t`Beta`}
            size="small"
            color="info"
            readOnly
            sx={{ position: "absolute", top: 8, right: 8 }}
          />
        )}
        <Box display="flex" justifyContent="center" alignItems="center" pt={2}>
          {/* @ts-expect-error WfSnail uses a discriminated union on workoutFamily but this component handles both cycling and running */}
          <WfSnail
            workoutFamily={workoutFamily}
            variant={WfSnailVariant.Small}
            empty={!hasProfile}
            dimensions={dimensions}
            i18nContext={i18n}
          />
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" gap={0.5} pt={1} pb={1}>
          <SportIcon fontSize="small" />
          <Typography variant="ui-xs-medium" color="text.secondary">
            {familyName}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" pb={2.5}>
          <Typography variant="ui-lg-bold" color={hasProfile ? "text.primary" : "text.disabled"}>
            {hasProfile ? fitnessProfileType?.name : t`Not set`}
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
}

export default AthleteProfilePreviewCard;
